import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';
import { zodEmailListResponseSchema } from './validations';

export type CompanyInvoiceMailbox = z.infer<typeof zodEmailListResponseSchema>;

/**
 * Fetches company invoice mailbox from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @returns The company invoice mailbox.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_invoice_mailbox_addresses_vadmin_company__company_id__invoice_mailbox_get
 */
export const fetchCompanyInvoiceMailbox = async (companyId: UUID) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.INVOICE_MAILBOX.GET(companyId), {
        validate: { response: zodEmailListResponseSchema },
      })
      .json<CompanyInvoiceMailbox>();
  } catch (error) {
    log.error({
      message: 'Error fetching invoice mailbox for company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the invoice mailbox for a company with a given companyId.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_invoice_mailbox_addresses_vadmin_company__company_id__invoice_mailbox_get
 */
export const useQueryCompanyInvoiceMailbox = (
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<CompanyInvoiceMailbox>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<CompanyInvoiceMailbox>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompanyInvoiceMailbox(companyId),
    queryKey: queryKeysCompany.getInvoiceMailbox(companyId),
    ...options,
  });
};
